/** Aspect ratio options for image elements. "auto" means no constraint. */
export const ASPECT_RATIO_OPTIONS = [
  { value: "1/1", label: "1:1", displayValue: "1:1" },
  { value: "3/2", label: "3:2", displayValue: "3:2" },
  { value: "4/3", label: "4:3", displayValue: "4:3" },
  { value: "4/5", label: "4:5", displayValue: "4:5" },
  { value: "16/9", label: "16:9", displayValue: "16:9" },
  { value: "auto", label: "Custom", displayValue: "Custom" },
] as const;

export type AspectRatioValue = (typeof ASPECT_RATIO_OPTIONS)[number]["value"];

/** Maps Tailwind aspect class names to ratio values */
export const TAILWIND_TO_RATIO: Record<string, string> = {
  square: "1/1",
  video: "16/9",
};

/** Maps ratio values to Tailwind aspect class names */
export const RATIO_TO_TAILWIND: Record<string, string> = {
  "1/1": "aspect-square",
  "16/9": "aspect-video",
};

/** Valid aspect ratio values as a Set for quick lookup */
export const VALID_ASPECT_RATIO_VALUES: Set<string> = new Set(
  ASPECT_RATIO_OPTIONS.map((option) => option.value),
);

/**
 * Converts a ratio value to the appropriate Tailwind class.
 * @param value - The aspect ratio value (e.g., "1/1", "16/9", "4/3", "auto")
 * @returns The Tailwind class (e.g., "aspect-square", "aspect-video", "aspect-[4/3]", "aspect-auto")
 */
export function ratioToTailwindClass(value: string): string {
  if (value === "auto") {
    return "aspect-auto";
  }
  return RATIO_TO_TAILWIND[value] ?? `aspect-[${value}]`;
}

/**
 * Extracts the ratio value from a Tailwind aspect class.
 * @param tailwindClass - The Tailwind class suffix (e.g., "square", "video", "[4/3]")
 * @returns The ratio value (e.g., "1/1", "16/9", "4/3") or null if invalid
 */
export function tailwindClassToRatio(
  tailwindClass: string,
): AspectRatioValue | null {
  // Check aliases first (square -> 1/1, video -> 16/9)
  if (tailwindClass in TAILWIND_TO_RATIO) {
    const value = TAILWIND_TO_RATIO[tailwindClass];
    return value ? (value as AspectRatioValue) : null;
  }

  // Handle "auto"
  if (tailwindClass === "auto") {
    return "auto";
  }

  // Handle arbitrary values like [4/3]
  const bracketMatch = tailwindClass.match(/^\[(.+)]$/);
  const resolvedValue = bracketMatch ? bracketMatch[1] : tailwindClass;

  if (resolvedValue && VALID_ASPECT_RATIO_VALUES.has(resolvedValue)) {
    return resolvedValue as AspectRatioValue;
  }

  return null;
}

/**
 * Extract aspect ratio value from a className string.
 * Handles Tailwind aspect-* classes including:
 * - Standard: aspect-square, aspect-video, aspect-auto
 * - Arbitrary: aspect-[4/3], aspect-[16/9]
 * - Important prefix: !aspect-square, !aspect-[4/3]
 */
export function extractAspectRatioFromClassName(
  className: string | null | undefined,
): AspectRatioValue | null {
  if (!className) {
    return null;
  }

  for (const cssClass of className.split(" ")) {
    const match = cssClass.match(/^!?aspect-(.+)$/);
    if (!match) {
      continue;
    }

    const aspectValue = match[1];
    if (aspectValue) {
      const ratio = tailwindClassToRatio(aspectValue);
      if (ratio) {
        return ratio;
      }
    }
  }

  return null;
}
